import { Button } from '@neo/primitives/button'
import { startCase } from 'lodash'

import { ACI_RETURN_TO_KEY } from 'v2/pages/settings/integrations/nango'
import { useAppNavigate } from 'v2/shared/hooks/use-app-navigate'

interface AppConnectionBannerProps {
  selectedAppProvider: string
  isConnected: boolean
  getAppLogo: (provider: string) => string | undefined
  urlSelectionType: string
}

export const AppConnectionBanner = ({
  selectedAppProvider,
  isConnected,
  getAppLogo,
  urlSelectionType,
}: AppConnectionBannerProps) => {
  const navigate = useAppNavigate()

  if (!selectedAppProvider) return null

  // Not connected banner
  if (!isConnected) {
    return (
      <div className="w-full max-w-[628px] mt-4 p-4 bg-bg2 rounded-md border border-st2 inline-flex flex-col justify-start items-start gap-4">
        <div className="inline-flex justify-start items-start gap-1">
          {getAppLogo(selectedAppProvider) ? (
            <img
              className="w-6 h-6"
              src={getAppLogo(selectedAppProvider)}
              alt={`${selectedAppProvider} logo`}
            />
          ) : (
            <img className="w-6 h-6" src="https://placehold.co/24x24" alt="placeholder" />
          )}
          <div className="flex justify-start items-center gap-1">
            <div className="inline-flex flex-col justify-center items-start gap-3">
              <div className="flex flex-col justify-start items-start gap-1">
                <div className="justify-start text-foreground text-p-600">
                  {startCase(selectedAppProvider)} is not connected
                </div>
                <div className="justify-start text-secondary text-xs-400">
                  To sync with {startCase(selectedAppProvider)}, connection to{' '}
                  {startCase(selectedAppProvider)} must be established first.
                </div>
              </div>
              <Button
                variant="default"
                size="sm"
                data-testid={`button-connect-${selectedAppProvider}`}
                onClick={() => {
                  // Stash WHERE the connect was started (this KB-create page) so the OAuth landing
                  // page returns the user here, not to /external-tools. Must set it BEFORE navigating
                  // away — redirectToOAuth on the connect page only sets it if unset (see nango.ts).
                  try {
                    sessionStorage.setItem(
                      ACI_RETURN_TO_KEY,
                      window.location.pathname + window.location.search,
                    )
                  } catch {
                    // ignore storage write failures (private mode, etc.)
                  }
                  navigate('/external-tools')
                }}
              >
                <span
                  data-testid="tooltip-connect-now"
                  title="Go to Integrations to connect this app"
                >
                  Connect now
                </span>
              </Button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Connected banner for "all" selection type
  if (urlSelectionType === 'all') {
    return (
      <div className="w-full max-w-[628px] mt-4 p-4 rounded-md border border-st2 bg-background inline-flex flex-col justify-start items-start gap-4">
        <div className="self-stretch inline-flex justify-start items-center gap-2">
          {getAppLogo(selectedAppProvider) ? (
            <img
              src={getAppLogo(selectedAppProvider)}
              alt={`${startCase(selectedAppProvider)} logo`}
              className="w-5 h-5 rounded"
            />
          ) : (
            <div className="w-5 h-5 relative bg-bg3 rounded flex items-center justify-center">
              <div className="text-foreground text-xs-600">
                {selectedAppProvider.charAt(0).toUpperCase()}
              </div>
            </div>
          )}
          <div className="flex justify-start items-center gap-1">
            <div className="inline-flex flex-col justify-center items-start gap-1">
              <div className="inline-flex justify-start items-start gap-1">
                <div className="justify-start text-foreground text-xs-500">
                  All content will be synced
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return null
}
